import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Switch,
  Button,
  Table,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Empty,
  Alert,
} from 'antd';
import { MedicineBoxOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  SurgeryProcedureType,
  PostOpComplication,
  type CreateSurgeryDto,
  type Surgery,
} from '../../../../api/dermatoApi';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const PROCEDURE_OPTIONS = Object.values(SurgeryProcedureType).map((v) => ({
  value: v,
  label:
    v === 'EXCISION_NARROW' ? 'Excizie îngustă'
    : v === 'EXCISION_WIDE' ? 'Excizie largă'
    : v === 'MOHS_SURGERY' ? 'Chirurgie Mohs'
    : v === 'FLAP_RECONSTRUCTION' ? 'Reconstrucție cu lambou'
    : v === 'GRAFT_RECONSTRUCTION' ? 'Reconstrucție cu grefă'
    : v === 'SENTINEL_LYMPH_NODE_BIOPSY' ? 'Biopsie ganglion santinelă'
    : v === 'LYMPHADENECTOMY' ? 'Limfadenectomie'
    : 'Alt tip',
}));

const COMPLICATION_OPTIONS = Object.values(PostOpComplication).map((v) => ({
  value: v,
  label:
    v === 'NONE' ? 'Fără complicații'
    : v === 'BLEEDING' ? 'Sângerare'
    : v === 'INFECTION' ? 'Infecție'
    : v === 'WOUND_DEHISCENCE' ? 'Dehiscența plăgii'
    : v === 'NECROSIS' ? 'Necroză'
    : v === 'SEROMA' ? 'Serom'
    : v === 'LYMPHEDEMA' ? 'Limfedem'
    : v === 'NERVE_DAMAGE' ? 'Leziune nervoasă'
    : 'Altă',
}));

const Step13Surgery = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { user } = useAuthStore();
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Surgery | null>(null);
  const [saving, setSaving] = useState(false);
  const [formAntd] = Form.useForm<CreateSurgeryDto>();

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  const refresh = async () => {
    if (!lesion?.id) return;
    setLoadingList(true);
    try {
      const list = await dermatoApi.surgeries.getByLesion(lesion.id);
      setSurgeries(list);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesion?.id]);

  const openCreate = () => {
    setEditing(null);
    formAntd.resetFields();
    formAntd.setFieldsValue({
      surgeryDate: dayjs(),
      procedureType: SurgeryProcedureType.EXCISION_WIDE,
      performedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
      unit: 'Chirurgie Plastică UMFVBT',
      location: lesion?.location ?? '',
      completed: true,
    } as any);
    setModalOpen(true);
  };

  const openEdit = (s: Surgery) => {
    setEditing(s);
    formAntd.setFieldsValue({
      ...s,
      surgeryDate: dayjs(s.surgeryDate),
      dischargeDate: s.dischargeDate ? dayjs(s.dischargeDate) : undefined,
    } as any);
    setModalOpen(true);
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!lesion?.id) return false;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateSurgeryDto = {
        ...values,
        surgeryDate: (values.surgeryDate as unknown as dayjs.Dayjs).toISOString(),
        dischargeDate: values.dischargeDate ? (values.dischargeDate as unknown as dayjs.Dayjs).toISOString() : undefined,
      };
      if (editing?.id) {
        await dermatoApi.surgeries.update(editing.id, dto);
      } else {
        await dermatoApi.surgeries.createForLesion(lesion.id, dto);
      }
      setModalOpen(false);
      await refresh();
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Salvarea pasului are loc în modal; în afara modalului nu există modificări nesalvate.
  const saveCurrentStep = async (): Promise<boolean> => {
    if (!modalOpen) return true;
    return handleSubmit();
  };

  useRegisterStepSave(saveCurrentStep);

  const handleDelete = async (id: string) => {
    await dermatoApi.surgeries.delete(id);
    await refresh();
  };

  if (loading) return <Card loading title="Chirurgie" />;

  if (lesions.length === 0) {
    return (
      <Card title="Chirurgie">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <MedicineBoxOutlined style={{ color: '#1890ff' }} />
          <span>Chirurgie — 1:N per leziune</span>
        </Space>
      }
      extra={
        lesion && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Adaugă chirurgie
          </Button>
        )
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {!lesion && <Alert type="warning" showIcon message="Selectează o leziune." />}

      {lesion && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text type="secondary">({surgeries.length} intervenții)</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          {loadingList ? (
            <Alert type="info" message="Se încarcă intervențiile…" />
          ) : surgeries.length === 0 ? (
            <Empty description="Nicio intervenție chirurgicală înregistrată." />
          ) : (
            <Table
              scroll={{ x: 'max-content' }}
              rowKey="id"
              dataSource={surgeries}
              size="small"
              pagination={false}
              columns={[
                { title: 'Data', dataIndex: 'surgeryDate', render: (v: string) => new Date(v).toLocaleDateString('ro-RO') },
                { title: 'Procedură', dataIndex: 'procedureType', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Operator', dataIndex: 'performedBy' },
                { title: 'Unitate', dataIndex: 'unit' },
                {
                  title: 'Complicații',
                  dataIndex: 'complications',
                  render: (v: string | null) => (v ? <Tag color="red">{v}</Tag> : <Tag color="green">Fără</Tag>),
                },
                {
                  title: 'Status',
                  dataIndex: 'completed',
                  render: (v: boolean) => (v ? <Tag color="green">Finalizat</Tag> : <Tag color="orange">În curs</Tag>),
                },
                {
                  title: '',
                  width: 100,
                  render: (_: unknown, s: Surgery) => (
                    <Space size="small">
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(s)} />
                      <Popconfirm title="Șterge intervenție?" onConfirm={() => void handleDelete(s.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </>
      )}

      <Modal
        title={editing ? 'Editare chirurgie' : 'Chirurgie nouă'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={saving}
        okText={editing ? 'Salvează' : 'Creează'}
        cancelText="Anulează"
        destroyOnClose
      >
        <Form form={formAntd} layout="vertical" preserve={false}>
          <Form.Item name="surgeryDate" label="Data chirurgiei" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="procedureType" label="Tip procedură" rules={[{ required: true }]}>
            <Select options={PROCEDURE_OPTIONS} />
          </Form.Item>
          <Form.Item name="performedBy" label="Chirurg operator" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="unit" label="Unitate" rules={[{ required: true }]}>
            <Input placeholder="ex: Chirurgie Plastică UMFVBT" />
          </Form.Item>
          <Form.Item name="location" label="Localizare" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="specimenSize" label="Dimensiune piesă (mm)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="plannedMargin" label="Margine planificată">
            <Input placeholder="ex: 5 mm" />
          </Form.Item>
          <Form.Item name="completed" label="Finalizat" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="complications" label="Complicații postoperatorii">
            <Select options={COMPLICATION_OPTIONS} allowClear />
          </Form.Item>
          <Form.Item name="postOpStatus" label="Status post-op">
            <Input placeholder="ex: Externat în ziua 2, evoluție favorabilă" />
          </Form.Item>
          <Form.Item name="recommendations" label="Recomandări">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="dischargeDate" label="Data externării">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="surgicalNotes" label="Note chirurgicale">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Step13Surgery;
